import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from './config';


const containerStyle = {
  padding: '40px',
  maxWidth: '800px',
  margin: '0 auto',
  width: '100%',
  color: 'var(--text-primary)'
};

const cardStyle = {
  background: 'var(--bg-secondary)',
  padding: '20px',
  borderRadius: '8px',
  border: '1px solid var(--border-color)',
  marginBottom: '20px'
};

const inputStyle = {
  width: '100%',
  padding: '10px',
  borderRadius: '4px',
  border: '1px solid var(--border-color)',
  background: 'var(--bg-input)',
  color: 'var(--text-primary)',
  marginBottom: '15px',
  boxSizing: 'border-box'
};

const btnStyle = (bg, disabled = false) => ({
  padding: '10px 20px',
  background: disabled ? 'var(--bg-tertiary)' : bg,
  color: disabled ? 'var(--text-tertiary)' : 'var(--text-inverse)',
  border: 'none',
  borderRadius: '4px',
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontSize: '14px',
  fontWeight: '500',
  transition: 'background 0.2s',
  width: '100%'
});

const OAUTH_PROVIDER_LABELS = {
  google: 'Google',
  microsoft: 'Microsoft',
  slack: 'Slack',
  github: 'GitHub',
  notion: 'Notion',
  hubspot: 'HubSpot',
  linear: 'Linear',
};

export default function AdminPanel({ onBack }) {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null); // { type: 'success' | 'error', text: '' }

  // OAuth App Management state
  const [oauthApps, setOauthApps] = useState([]);
  const [oauthLoading, setOauthLoading] = useState(false);
  const [oauthMessage, setOauthMessage] = useState(null);
  const [showOAuthForm, setShowOAuthForm] = useState(false);
  const [editingOAuthApp, setEditingOAuthApp] = useState(null); // null = create, object = edit
  const [oauthForm, setOauthForm] = useState({ provider: 'google', client_id: '', client_secret: '', scopes: '' });
  const [oauthRedirectUris, setOauthRedirectUris] = useState({}); // provider -> redirect_uri
  const [oauthBundledProviders, setOauthBundledProviders] = useState(new Set()); // providers using bundled credentials

  // SSO / SAML / LDAP state
  const [ssoConfigs, setSSOConfigs] = useState([]);
  const [ssoLoading, setSSOLoading] = useState(false);
  const [ssoMessage, setSSOMessage] = useState(null);
  const [showSSOForm, setShowSSOForm] = useState(false);
  const [editingSSOProvider, setEditingSSOProvider] = useState(null);

  // Branding state
  const [branding, setBranding] = useState({ app_name: '', logo_url: '', favicon_url: '', accent_color: '', footer_text: '' });
  const [brandingMsg, setBrandingMsg] = useState(null);
  const [brandingSaving, setBrandingSaving] = useState(false);

  // Network Policy state
  const [netPolicy, setNetPolicy] = useState({ enabled: false, cidr_allowlist: '', public_endpoint_bypass: true, trusted_proxies: '' });
  const [netPolicyMsg, setNetPolicyMsg] = useState(null);
  const [netPolicySaving, setNetPolicySaving] = useState(false);

  // SIEM Streaming state
  const [siemConfig, setSiemConfig] = useState({ destination_url: '', secret_header_name: '', secret_header_value: '', enabled: true, event_types: '' });
  const [siemMsg, setSiemMsg] = useState(null);
  const [siemSaving, setSiemSaving] = useState(false);
  const [siemConfigured, setSiemConfigured] = useState(false);
  const _defaultSSOForm = {
    provider: 'oidc', label: '', enabled: true, enforce: false,
    discovery_url: '', client_id: '', client_secret: '', scope: 'openid email profile',
    sp_entity_id: '', idp_entity_id: '', idp_sso_url: '', idp_x509cert: '',
    host: '', port: '389', base_dn: '', user_search_filter: '(sAMAccountName={username})',
    bind_dn: '', bind_password: '', use_ssl: false,
  };
  const [ssoForm, setSSOForm] = useState(_defaultSSOForm);

  const fetchBranding = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/admin/branding`, { headers: { Authorization: `Bearer ${token}` } });
      setBranding({ app_name: res.data.app_name || '', logo_url: res.data.logo_url || '', favicon_url: res.data.favicon_url || '', accent_color: res.data.accent_color || '', footer_text: res.data.footer_text || '' });
    } catch (err) { console.error('Failed to fetch branding', err); }
  }, []);

  const fetchNetPolicy = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/admin/network-policy`, { headers: { Authorization: `Bearer ${token}` } });
      setNetPolicy({
        enabled: res.data.enabled,
        cidr_allowlist: (res.data.cidr_allowlist || []).join('\n'),
        public_endpoint_bypass: res.data.public_endpoint_bypass,
        trusted_proxies: (res.data.trusted_proxies || []).join('\n'),
      });
    } catch (err) { console.error('Failed to fetch network policy', err); }
  }, []);

  const fetchSiemConfig = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/admin/audit/stream-config`, { headers: { Authorization: `Bearer ${token}` } });
      setSiemConfigured(res.data.configured || false);
      if (res.data.configured) {
        setSiemConfig({
          destination_url: res.data.destination_url || '',
          secret_header_name: res.data.secret_header_name || '',
          secret_header_value: '',
          enabled: res.data.enabled,
          event_types: (res.data.event_types || []).join(', '),
        });
      }
    } catch (err) { console.error('Failed to fetch SIEM config', err); }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchOAuthApps();
    fetchSSOConfigs();
    fetchBranding();
    fetchNetPolicy();
    fetchSiemConfig();
  }, [fetchBranding, fetchNetPolicy, fetchSiemConfig]);

  const fetchOAuthApps = async () => {
    setOauthLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [appsRes, providersRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/admin/oauth-apps`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_BASE_URL}/oauth/providers`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setOauthApps(appsRes.data);
      const uriMap = {};
      const bundled = new Set();
      for (const p of providersRes.data) {
        if (p.redirect_uri) uriMap[p.provider] = p.redirect_uri;
        if (p.is_bundled) bundled.add(p.provider);
      }
      setOauthRedirectUris(uriMap);
      setOauthBundledProviders(bundled);
    } catch (err) {
      console.error('Failed to fetch OAuth apps', err);
    } finally {
      setOauthLoading(false);
    }
  };

  const handleOAuthSubmit = async (e) => {
    e.preventDefault();
    setOauthMessage(null);
    try {
      const token = localStorage.getItem('token');
      if (editingOAuthApp) {
        const body = { client_id: oauthForm.client_id, scopes: oauthForm.scopes || null };
        if (oauthForm.client_secret) body.client_secret = oauthForm.client_secret;
        await axios.put(`${API_BASE_URL}/admin/oauth-apps/${editingOAuthApp.id}`, body, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setOauthMessage({ type: 'success', text: 'OAuth app updated.' });
      } else {
        await axios.post(`${API_BASE_URL}/admin/oauth-apps`, {
          provider: oauthForm.provider,
          client_id: oauthForm.client_id,
          client_secret: oauthForm.client_secret,
          scopes: oauthForm.scopes || null,
        }, { headers: { Authorization: `Bearer ${token}` } });
        setOauthMessage({ type: 'success', text: `OAuth app for ${OAUTH_PROVIDER_LABELS[oauthForm.provider] || oauthForm.provider} registered.` });
      }
      setShowOAuthForm(false);
      setEditingOAuthApp(null);
      setOauthForm({ provider: 'google', client_id: '', client_secret: '', scopes: '' });
      fetchOAuthApps();
    } catch (err) {
      setOauthMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to save OAuth app.' });
    }
  };

  const handleOAuthDelete = async (appId, provider) => {
    if (!(await window.confirm(`Remove OAuth app for ${OAUTH_PROVIDER_LABELS[provider] || provider}?`))) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/admin/oauth-apps/${appId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOauthApps(oauthApps.filter(a => a.id !== appId));
      setOauthMessage({ type: 'success', text: 'OAuth app removed.' });
    } catch (err) {
      setOauthMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to delete OAuth app.' });
    }
  };

  const handleOAuthToggle = async (app) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE_URL}/admin/oauth-apps/${app.id}`, { enabled: !app.enabled }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOauthApps(oauthApps.map(a => a.id === app.id ? { ...a, enabled: !a.enabled } : a));
    } catch (err) {
      setOauthMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to toggle.' });
    }
  };

  const fetchSSOConfigs = async () => {
    setSSOLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/admin/sso/configs`, { headers: { Authorization: `Bearer ${token}` } });
      setSSOConfigs(res.data);
    } catch (err) {
      console.error('Failed to fetch SSO configs', err);
    } finally {
      setSSOLoading(false);
    }
  };

  const handleSSOSave = async (e) => {
    e.preventDefault();
    setSSOMessage(null);
    const { provider, enabled, enforce, label, ...rest } = ssoForm;
    // Build config based on provider type
    let config = { label };
    if (provider === 'oidc') {
      config = { label, discovery_url: rest.discovery_url, client_id: rest.client_id, client_secret: rest.client_secret, scope: rest.scope };
    } else if (provider === 'saml') {
      config = { label, sp_entity_id: rest.sp_entity_id, idp_entity_id: rest.idp_entity_id, idp_sso_url: rest.idp_sso_url, idp_x509cert: rest.idp_x509cert };
    } else if (provider === 'ldap') {
      config = { host: rest.host, port: rest.port, base_dn: rest.base_dn, user_search_filter: rest.user_search_filter, bind_dn: rest.bind_dn, bind_password: rest.bind_password, use_ssl: rest.use_ssl };
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/admin/sso/config`, { provider, config, enabled, enforce }, { headers: { Authorization: `Bearer ${token}` } });
      setSSOMessage({ type: 'success', text: `${provider.toUpperCase()} SSO configuration saved.` });
      setShowSSOForm(false);
      setEditingSSOProvider(null);
      fetchSSOConfigs();
    } catch (err) {
      setSSOMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to save SSO config.' });
    }
  };

  const handleSSODelete = async (provider) => {
    if (!(await window.confirm(`Remove ${provider.toUpperCase()} SSO configuration?`))) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/admin/sso/config/${provider}`, { headers: { Authorization: `Bearer ${token}` } });
      setSSOConfigs(ssoConfigs.filter(c => c.provider !== provider));
      setSSOMessage({ type: 'success', text: `${provider.toUpperCase()} SSO removed.` });
    } catch (err) {
      setSSOMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to delete SSO config.' });
    }
  };

  const handleSSOTest = async () => {
    setSSOMessage(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_BASE_URL}/admin/sso/test/${ssoForm.provider}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setSSOMessage({ type: 'success', text: res.data.detail || 'Connection successful.' });
    } catch (err) {
      setSSOMessage({ type: 'error', text: err.response?.data?.detail || 'Connection test failed.' });
    }
  };

  const handleSSOEdit = (cfg) => {
    setEditingSSOProvider(cfg.provider);
    setSSOForm({
      ..._defaultSSOForm,
      provider: cfg.provider,
      enabled: cfg.enabled,
      enforce: cfg.enforce,
      ...cfg.config,
    });
    setShowSSOForm(true);
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error("Failed to fetch users", error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!(await window.confirm("Are you sure you want to delete this user?"))) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(users.filter(u => u.id !== userId));
      setMessage({ type: 'success', text: 'User deleted successfully' });
    } catch (error) {
      console.error("Failed to delete user", error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || "Failed to delete user" 
      });
    }
  };

  const handleToggleAdmin = async (user) => {
    try {
      const token = localStorage.getItem('token');
      const newIsAdmin = !user.is_admin;
      await axios.put(`${API_BASE_URL}/admin/users/${user.id}/role`, 
        { is_admin: newIsAdmin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsers(users.map(u => u.id === user.id ? { ...u, is_admin: newIsAdmin } : u));
      setMessage({ type: 'success', text: `User role updated successfully` });
    } catch (error) {
      console.error("Failed to update user role", error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || "Failed to update user role" 
      });
    }
  };
  
  const handleCreateUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/users/`, {
        username,
        email,
        password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: `User ${username} created successfully!` });
      setUsername('');
      setEmail('');
      setPassword('');
      fetchUsers(); // Refresh list
    } catch (error) {
      console.error("Failed to create user", error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || "Failed to create user" 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ width: '100vw', height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-primary)', overflow: 'auto' }}>
      <div style={{ padding: '20px', borderBottom: '1px solid var(--border-color)', background: 'var(--bg-tertiary)', display: 'flex', alignItems: 'center' }}>
        <button onClick={onBack} style={{ background: 'transparent', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', marginRight: '10px', fontSize: '16px' }}>
          ← Back
        </button>
        <h2 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '20px' }}>Admin Panel</h2>
      </div>

      <div style={containerStyle}>
        <div style={cardStyle}>
          <h3 style={{ marginTop: 0, marginBottom: '20px', color: 'var(--text-primary)' }}>Create New User</h3>
          
          {message && (
            <div style={{ 
              padding: '10px', 
              borderRadius: '4px', 
              marginBottom: '20px',
              background: message.type === 'success' ? 'rgba(40, 167, 69, 0.1)' : 'rgba(220, 53, 69, 0.1)',
              color: message.type === 'success' ? 'var(--success-color)' : 'var(--error-color)',
              border: `1px solid ${message.type === 'success' ? 'var(--success-color)' : 'var(--error-color)'}`
            }}>
              {message.text}
            </div>
          )}

          <form onSubmit={handleCreateUser}>
            <div style={{ marginBottom: '5px', color: 'var(--text-secondary)' }}>Username</div>
            <input 
              type="text" 
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              style={inputStyle}
              required
              placeholder="jdoe"
            />

            <div style={{ marginBottom: '5px', color: 'var(--text-secondary)' }}>Email</div>
            <input 
              type="email" 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={inputStyle}
              placeholder="jdoe@example.com"
            />

            <div style={{ marginBottom: '5px', color: 'var(--text-secondary)' }}>Password</div>
            <input 
              type="password" 
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={inputStyle}
              required
              placeholder="••••••••"
            />

            <button type="submit" disabled={loading} style={btnStyle('var(--accent-color)', loading)}>
              {loading ? 'Creating...' : 'Create User'}
            </button>
          </form>
        </div>
        
        <div style={{ color: 'var(--text-tertiary)', fontSize: '14px', textAlign: 'center' }}>
          Only administrators can access this page.
        </div>

        <div style={cardStyle}>
          <h3 style={{ marginTop: 0, marginBottom: '20px', color: 'var(--text-primary)' }}>Existing Users</h3>
          
          {loadingUsers ? (
            <div style={{ color: 'var(--text-tertiary)', textAlign: 'center', padding: '20px' }}>Loading users...</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', color: 'var(--text-primary)' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border-color)', textAlign: 'left' }}>
                    <th style={{ padding: '10px' }}>ID</th>
                    <th style={{ padding: '10px' }}>Username</th>
                    <th style={{ padding: '10px' }}>Email</th>
                    <th style={{ padding: '10px' }}>Admin</th>
                    <th style={{ padding: '10px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td style={{ padding: '10px' }}>{user.id}</td>
                      <td style={{ padding: '10px' }}>{user.username}</td>
                      <td style={{ padding: '10px' }}>{user.email || '-'}</td>
                      <td style={{ padding: '10px' }}>
                        <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                          <input 
                            type="checkbox" 
                            checked={user.is_admin} 
                            onChange={() => handleToggleAdmin(user)}
                            style={{ marginRight: '5px' }}
                          />
                          {user.is_admin ? 'Yes' : 'No'}
                        </label>
                      </td>
                      <td style={{ padding: '10px' }}>
                        <button 
                          onClick={() => handleDeleteUser(user.id)}
                          style={{ 
                            background: 'var(--error-color)', 
                            color: 'var(--text-inverse)', 
                            border: 'none', 
                            padding: '5px 10px', 
                            borderRadius: '4px', 
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Delete User
                        </button>
                      </td>
                    </tr>
                  ))}
                  {users.length === 0 && (
                    <tr>
                      <td colSpan="5" style={{ padding: '20px', textAlign: 'center', color: 'var(--text-tertiary)' }}>
                        No users found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* OAuth App Management */}
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ margin: 0, color: 'var(--text-primary)' }}>OAuth App Management</h3>
            {!showOAuthForm && (
              <button
                onClick={() => { setEditingOAuthApp(null); setOauthForm({ provider: 'google', client_id: '', client_secret: '', scopes: '' }); setShowOAuthForm(true); }}
                style={{ ...btnStyle('var(--accent-color)'), width: 'auto', padding: '8px 16px', fontSize: '13px' }}
              >
                + Register OAuth App
              </button>
            )}
          </div>
          <p style={{ margin: '0 0 16px', fontSize: '13px', color: 'var(--text-tertiary)' }}>
            Register your OAuth app client credentials here. Users can then click "Connect with [Provider]" in the
            Credential Manager instead of manually copying tokens.
          </p>

          {oauthMessage && (
            <div style={{
              padding: '10px', borderRadius: '4px', marginBottom: '16px',
              background: oauthMessage.type === 'success' ? 'rgba(40,167,69,0.1)' : 'rgba(220,53,69,0.1)',
              color: oauthMessage.type === 'success' ? 'var(--success-color)' : 'var(--error-color)',
              border: `1px solid ${oauthMessage.type === 'success' ? 'var(--success-color)' : 'var(--error-color)'}`
            }}>
              {oauthMessage.text}
            </div>
          )}

          {showOAuthForm && (
            <form onSubmit={handleOAuthSubmit} style={{ background: 'var(--bg-primary)', padding: '16px', borderRadius: '6px', marginBottom: '20px', border: '1px solid var(--border-color)' }}>
              <h4 style={{ margin: '0 0 16px', color: 'var(--text-primary)' }}>
                {editingOAuthApp ? `Edit: ${OAUTH_PROVIDER_LABELS[editingOAuthApp.provider] || editingOAuthApp.provider}` : 'Register New OAuth App'}
              </h4>
              {!editingOAuthApp && (
                <div>
                  <div style={{ marginBottom: '5px', color: 'var(--text-secondary)', fontSize: '13px' }}>Provider</div>
                  <select
                    value={oauthForm.provider}
                    onChange={e => setOauthForm({ ...oauthForm, provider: e.target.value })}
                    style={inputStyle}
                    required
                  >
                    {Object.entries(OAUTH_PROVIDER_LABELS).map(([v, l]) => (
                      <option key={v} value={v}>{l}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Redirect URI hint — shown for both create and edit */}
              {oauthRedirectUris[editingOAuthApp?.provider || oauthForm.provider] && (
                <div style={{ marginBottom: '15px', padding: '10px 12px', background: 'var(--bg-secondary)', borderRadius: '6px', border: '1px solid var(--border-color)' }}>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px', fontWeight: 500 }}>
                    Redirect URI — add this to your {OAUTH_PROVIDER_LABELS[editingOAuthApp?.provider || oauthForm.provider]} OAuth app:
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <code style={{ flex: 1, fontSize: '12px', fontFamily: 'monospace', color: 'var(--accent-color)', wordBreak: 'break-all' }}>
                      {oauthRedirectUris[editingOAuthApp?.provider || oauthForm.provider]}
                    </code>
                    <button
                      type="button"
                      onClick={() => navigator.clipboard?.writeText(oauthRedirectUris[editingOAuthApp?.provider || oauthForm.provider])}
                      style={{ flexShrink: 0, background: 'var(--button-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                    >
                      Copy
                    </button>
                  </div>
                </div>
              )}
              <div>
                <div style={{ marginBottom: '5px', color: 'var(--text-secondary)', fontSize: '13px' }}>Client ID</div>
                <input
                  type="text"
                  value={oauthForm.client_id}
                  onChange={e => setOauthForm({ ...oauthForm, client_id: e.target.value })}
                  style={inputStyle}
                  required
                  placeholder="Your OAuth app client ID"
                />
              </div>
              <div>
                <div style={{ marginBottom: '5px', color: 'var(--text-secondary)', fontSize: '13px' }}>
                  Client Secret{editingOAuthApp ? ' (leave blank to keep existing)' : ''}
                </div>
                <input
                  type="password"
                  value={oauthForm.client_secret}
                  onChange={e => setOauthForm({ ...oauthForm, client_secret: e.target.value })}
                  style={inputStyle}
                  required={!editingOAuthApp}
                  placeholder={editingOAuthApp ? '••••••••' : 'Your OAuth app client secret'}
                />
              </div>
              <div>
                <div style={{ marginBottom: '5px', color: 'var(--text-secondary)', fontSize: '13px' }}>
                  Scopes (optional — leave blank to use provider defaults)
                </div>
                <input
                  type="text"
                  value={oauthForm.scopes}
                  onChange={e => setOauthForm({ ...oauthForm, scopes: e.target.value })}
                  style={inputStyle}
                  placeholder="space-separated scopes"
                />
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button type="submit" style={{ ...btnStyle('var(--accent-color)'), width: 'auto', padding: '8px 20px', fontSize: '13px' }}>
                  {editingOAuthApp ? 'Save Changes' : 'Register'}
                </button>
                <button type="button" onClick={() => { setShowOAuthForm(false); setEditingOAuthApp(null); }} style={{ ...btnStyle('var(--button-bg)'), width: 'auto', padding: '8px 20px', fontSize: '13px', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}>
                  Cancel
                </button>
              </div>
            </form>
          )}

          {/* Bundled OAuth info banner */}
          {oauthBundledProviders.size > 0 && (
            <div style={{
              padding: '10px 14px', borderRadius: '6px', marginBottom: '14px',
              background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)',
              fontSize: '12px', color: 'var(--text-secondary)',
            }}>
              <strong>Built-in OAuth available</strong> for: {[...oauthBundledProviders].map(p => OAUTH_PROVIDER_LABELS[p] || p).join(', ')}.
              Users can connect with one click using xavvy's bundled credentials. Register your own app below for custom branding.
            </div>
          )}

          {oauthLoading ? (
            <div style={{ color: 'var(--text-tertiary)', textAlign: 'center', padding: '20px' }}>Loading...</div>
          ) : oauthApps.length === 0 && oauthBundledProviders.size === 0 ? (
            <div style={{ color: 'var(--text-tertiary)', textAlign: 'center', padding: '20px', border: '1px dashed var(--border-color)', borderRadius: '6px' }}>
              No OAuth apps registered yet. Register an app above, or set XAVVY_BUNDLED_OAUTH_* environment variables for zero-config OAuth.
            </div>
          ) : oauthApps.length === 0 ? (
            <div style={{ color: 'var(--text-tertiary)', textAlign: 'center', padding: '20px', border: '1px dashed var(--border-color)', borderRadius: '6px' }}>
              No custom OAuth apps registered. Users are using built-in xavvy OAuth credentials.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', color: 'var(--text-primary)', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-color)', textAlign: 'left' }}>
                  <th style={{ padding: '10px' }}>Provider</th>
                  <th style={{ padding: '10px' }}>Client ID</th>
                  <th style={{ padding: '10px' }}>Scopes</th>
                  <th style={{ padding: '10px' }}>Enabled</th>
                  <th style={{ padding: '10px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {oauthApps.map(app => (
                  <tr key={app.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                    <td style={{ padding: '10px', fontWeight: 500 }}>
                      {OAUTH_PROVIDER_LABELS[app.provider] || app.provider}
                      <span style={{ marginLeft: '6px', fontSize: '10px', padding: '1px 5px', borderRadius: '3px', background: 'rgba(34,197,94,0.15)', color: '#22c55e', fontWeight: 400 }}>Custom</span>
                    </td>
                    <td style={{ padding: '10px', fontFamily: 'monospace', color: 'var(--text-tertiary)' }}>{app.client_id}</td>
                    <td style={{ padding: '10px', color: 'var(--text-tertiary)' }}>{app.scopes || <em>default</em>}</td>
                    <td style={{ padding: '10px' }}>
                      <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                        <input type="checkbox" checked={app.enabled} onChange={() => handleOAuthToggle(app)} style={{ marginRight: '5px' }} />
                        {app.enabled ? 'Yes' : 'No'}
                      </label>
                    </td>
                    <td style={{ padding: '10px', display: 'flex', gap: '6px' }}>
                      <button
                        onClick={() => { setEditingOAuthApp(app); setOauthForm({ provider: app.provider, client_id: app.client_id, client_secret: '', scopes: app.scopes || '' }); setShowOAuthForm(true); }}
                        style={{ background: 'var(--accent-color)', color: 'var(--text-inverse)', border: 'none', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                      >Edit</button>
                      <button
                        onClick={() => handleOAuthDelete(app.id, app.provider)}
                        style={{ background: 'var(--error-color)', color: 'var(--text-inverse)', border: 'none', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                      >Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* SSO / SAML / LDAP Settings */}
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ margin: 0, color: 'var(--text-primary)' }}>SSO / SAML / LDAP Settings</h3>
            {!showSSOForm && (
              <button
                onClick={() => { setEditingSSOProvider(null); setSSOForm(_defaultSSOForm); setShowSSOForm(true); setSSOMessage(null); }}
                style={{ ...btnStyle('var(--accent-color)'), width: 'auto', padding: '8px 16px', fontSize: '13px' }}
              >+ Add SSO Provider</button>
            )}
          </div>
          <p style={{ margin: '0 0 16px', fontSize: '13px', color: 'var(--text-tertiary)' }}>
            Configure OIDC, SAML 2.0, or LDAP/AD identity providers. Once enabled, a "Sign in with SSO" button
            appears on the login page. Enabling "Enforce SSO" disables local password logins for all users.
          </p>

          {ssoMessage && (
            <div style={{ padding: '10px', borderRadius: '4px', marginBottom: '16px', background: ssoMessage.type === 'success' ? 'rgba(40,167,69,0.1)' : 'rgba(220,53,69,0.1)', color: ssoMessage.type === 'success' ? 'var(--success-color)' : 'var(--error-color)', border: `1px solid ${ssoMessage.type === 'success' ? 'var(--success-color)' : 'var(--error-color)'}` }}>
              {ssoMessage.text}
            </div>
          )}

          {showSSOForm && (
            <form onSubmit={handleSSOSave} style={{ background: 'var(--bg-primary)', padding: '16px', borderRadius: '6px', marginBottom: '20px', border: '1px solid var(--border-color)' }}>
              <h4 style={{ margin: '0 0 16px', color: 'var(--text-primary)' }}>
                {editingSSOProvider ? `Edit: ${editingSSOProvider.toUpperCase()} SSO` : 'Add SSO Provider'}
              </h4>

              {!editingSSOProvider && (
                <div>
                  <div style={{ marginBottom: '5px', color: 'var(--text-secondary)', fontSize: '13px' }}>Provider Type</div>
                  <select value={ssoForm.provider} onChange={e => setSSOForm({ ..._defaultSSOForm, provider: e.target.value })} style={inputStyle} required>
                    <option value="oidc">OIDC (OpenID Connect) — Google, Microsoft, Okta, Auth0, custom</option>
                    <option value="saml">SAML 2.0 — Okta, Azure AD, OneLogin, any SAML IdP</option>
                    <option value="ldap">LDAP / Active Directory</option>
                  </select>
                </div>
              )}

              {/* OIDC fields */}
              {ssoForm.provider === 'oidc' && (
                <>
                  <div style={{ marginBottom: '5px', color: 'var(--text-secondary)', fontSize: '13px' }}>Button Label (shown on login page)</div>
                  <input type="text" value={ssoForm.label} onChange={e => setSSOForm({ ...ssoForm, label: e.target.value })} style={inputStyle} placeholder="Sign in with Okta" />
                  <div style={{ marginBottom: '5px', color: 'var(--text-secondary)', fontSize: '13px' }}>Discovery URL <span style={{ color: 'var(--text-tertiary)' }}>(e.g. https://accounts.google.com/.well-known/openid-configuration)</span></div>
                  <input type="url" value={ssoForm.discovery_url} onChange={e => setSSOForm({ ...ssoForm, discovery_url: e.target.value })} style={inputStyle} placeholder="https://your-idp/.well-known/openid-configuration" required />
                  <div style={{ marginBottom: '5px', color: 'var(--text-secondary)', fontSize: '13px' }}>Client ID</div>
                  <input type="text" value={ssoForm.client_id} onChange={e => setSSOForm({ ...ssoForm, client_id: e.target.value })} style={inputStyle} required />
                  <div style={{ marginBottom: '5px', color: 'var(--text-secondary)', fontSize: '13px' }}>Client Secret{editingSSOProvider ? ' (leave blank to keep existing)' : ''}</div>
                  <input type="password" value={ssoForm.client_secret} onChange={e => setSSOForm({ ...ssoForm, client_secret: e.target.value })} style={inputStyle} required={!editingSSOProvider} placeholder={editingSSOProvider ? '••••••••' : ''} />
                  <div style={{ marginBottom: '5px', color: 'var(--text-secondary)', fontSize: '13px' }}>Scope</div>
                  <input type="text" value={ssoForm.scope} onChange={e => setSSOForm({ ...ssoForm, scope: e.target.value })} style={inputStyle} placeholder="openid email profile" />
                  <div style={{ padding: '10px 12px', background: 'var(--bg-secondary)', borderRadius: '6px', border: '1px solid var(--border-color)', marginBottom: '15px', fontSize: '12px', color: 'var(--text-tertiary)' }}>
                    Callback URL to register in your IdP: <code style={{ color: 'var(--accent-color)' }}>{API_BASE_URL}/auth/oidc/callback</code>
                  </div>
                </>
              )}

              {/* SAML fields */}
              {ssoForm.provider === 'saml' && (
                <>
                  <div style={{ marginBottom: '5px', color: 'var(--text-secondary)', fontSize: '13px' }}>Button Label</div>
                  <input type="text" value={ssoForm.label} onChange={e => setSSOForm({ ...ssoForm, label: e.target.value })} style={inputStyle} placeholder="Sign in with SAML SSO" />
                  <div style={{ marginBottom: '5px', color: 'var(--text-secondary)', fontSize: '13px' }}>SP Entity ID <span style={{ color: 'var(--text-tertiary)' }}>(leave blank to use default)</span></div>
                  <input type="text" value={ssoForm.sp_entity_id} onChange={e => setSSOForm({ ...ssoForm, sp_entity_id: e.target.value })} style={inputStyle} placeholder={`${API_BASE_URL}/auth/saml/metadata`} />
                  <div style={{ marginBottom: '5px', color: 'var(--text-secondary)', fontSize: '13px' }}>IdP Entity ID</div>
                  <input type="text" value={ssoForm.idp_entity_id} onChange={e => setSSOForm({ ...ssoForm, idp_entity_id: e.target.value })} style={inputStyle} required />
                  <div style={{ marginBottom: '5px', color: 'var(--text-secondary)', fontSize: '13px' }}>IdP SSO URL</div>
                  <input type="url" value={ssoForm.idp_sso_url} onChange={e => setSSOForm({ ...ssoForm, idp_sso_url: e.target.value })} style={inputStyle} required />
                  <div style={{ marginBottom: '5px', color: 'var(--text-secondary)', fontSize: '13px' }}>IdP x509 Certificate (PEM, without header/footer)</div>
                  <textarea value={ssoForm.idp_x509cert} onChange={e => setSSOForm({ ...ssoForm, idp_x509cert: e.target.value })} style={{ ...inputStyle, fontFamily: 'monospace', fontSize: '11px', height: '120px', resize: 'vertical' }} placeholder="MIIC..." required />
                  <div style={{ padding: '10px 12px', background: 'var(--bg-secondary)', borderRadius: '6px', border: '1px solid var(--border-color)', marginBottom: '15px', fontSize: '12px', color: 'var(--text-tertiary)' }}>
                    SP Metadata URL: <code style={{ color: 'var(--accent-color)' }}>{API_BASE_URL}/auth/saml/metadata</code>
                    &nbsp;· ACS URL: <code style={{ color: 'var(--accent-color)' }}>{API_BASE_URL}/auth/saml/acs</code>
                    &nbsp;· Requires <code>python3-saml</code> installed on the server.
                  </div>
                </>
              )}

              {/* LDAP fields */}
              {ssoForm.provider === 'ldap' && (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: '10px' }}>
                    <div>
                      <div style={{ marginBottom: '5px', color: 'var(--text-secondary)', fontSize: '13px' }}>LDAP Host</div>
                      <input type="text" value={ssoForm.host} onChange={e => setSSOForm({ ...ssoForm, host: e.target.value })} style={inputStyle} placeholder="ldap.corp.example.com" required />
                    </div>
                    <div>
                      <div style={{ marginBottom: '5px', color: 'var(--text-secondary)', fontSize: '13px' }}>Port</div>
                      <input type="number" value={ssoForm.port} onChange={e => setSSOForm({ ...ssoForm, port: e.target.value })} style={inputStyle} placeholder="389" />
                    </div>
                  </div>
                  <div style={{ marginBottom: '5px', color: 'var(--text-secondary)', fontSize: '13px' }}>Base DN</div>
                  <input type="text" value={ssoForm.base_dn} onChange={e => setSSOForm({ ...ssoForm, base_dn: e.target.value })} style={inputStyle} placeholder="DC=corp,DC=example,DC=com" required />
                  <div style={{ marginBottom: '5px', color: 'var(--text-secondary)', fontSize: '13px' }}>User Search Filter <span style={{ color: 'var(--text-tertiary)' }}>({'{username}'} is replaced with the login name)</span></div>
                  <input type="text" value={ssoForm.user_search_filter} onChange={e => setSSOForm({ ...ssoForm, user_search_filter: e.target.value })} style={inputStyle} placeholder="(sAMAccountName={username})" required />
                  <div style={{ marginBottom: '5px', color: 'var(--text-secondary)', fontSize: '13px' }}>Bind DN <span style={{ color: 'var(--text-tertiary)' }}>(service account for user search — optional for anonymous)</span></div>
                  <input type="text" value={ssoForm.bind_dn} onChange={e => setSSOForm({ ...ssoForm, bind_dn: e.target.value })} style={inputStyle} placeholder="CN=svc-xavvy,OU=ServiceAccounts,DC=corp,DC=example,DC=com" />
                  <div style={{ marginBottom: '5px', color: 'var(--text-secondary)', fontSize: '13px' }}>Bind Password{editingSSOProvider ? ' (leave blank to keep existing)' : ''}</div>
                  <input type="password" value={ssoForm.bind_password} onChange={e => setSSOForm({ ...ssoForm, bind_password: e.target.value })} style={inputStyle} placeholder={editingSSOProvider ? '••••••••' : ''} />
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '15px', cursor: 'pointer', fontSize: '13px', color: 'var(--text-secondary)' }}>
                    <input type="checkbox" checked={ssoForm.use_ssl} onChange={e => setSSOForm({ ...ssoForm, use_ssl: e.target.checked })} />
                    Use LDAPS (SSL, port 636)
                  </label>
                </>
              )}

              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '15px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: 'var(--text-secondary)' }}>
                  <input type="checkbox" checked={ssoForm.enabled} onChange={e => setSSOForm({ ...ssoForm, enabled: e.target.checked })} />
                  Enabled
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: 'var(--text-secondary)' }}>
                  <input type="checkbox" checked={ssoForm.enforce} onChange={e => setSSOForm({ ...ssoForm, enforce: e.target.checked })} />
                  Enforce SSO (disable local password login)
                </label>
              </div>

              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button type="submit" style={{ ...btnStyle('var(--accent-color)'), width: 'auto', padding: '8px 20px', fontSize: '13px' }}>
                  {editingSSOProvider ? 'Save Changes' : 'Save'}
                </button>
                {ssoForm.provider === 'ldap' && (
                  <button type="button" onClick={handleSSOTest} style={{ ...btnStyle('var(--button-bg)'), width: 'auto', padding: '8px 20px', fontSize: '13px', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}>
                    Test Connection
                  </button>
                )}
                <button type="button" onClick={() => { setShowSSOForm(false); setEditingSSOProvider(null); setSSOMessage(null); }} style={{ ...btnStyle('var(--button-bg)'), width: 'auto', padding: '8px 20px', fontSize: '13px', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}>
                  Cancel
                </button>
              </div>
            </form>
          )}

          {ssoLoading ? (
            <div style={{ color: 'var(--text-tertiary)', textAlign: 'center', padding: '20px' }}>Loading...</div>
          ) : ssoConfigs.length === 0 && !showSSOForm ? (
            <div style={{ color: 'var(--text-tertiary)', textAlign: 'center', padding: '20px', border: '1px dashed var(--border-color)', borderRadius: '6px' }}>
              No SSO providers configured.
            </div>
          ) : ssoConfigs.length > 0 ? (
            <table style={{ width: '100%', borderCollapse: 'collapse', color: 'var(--text-primary)', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-color)', textAlign: 'left' }}>
                  <th style={{ padding: '10px' }}>Provider</th>
                  <th style={{ padding: '10px' }}>Label / Host</th>
                  <th style={{ padding: '10px' }}>Enabled</th>
                  <th style={{ padding: '10px' }}>Enforce</th>
                  <th style={{ padding: '10px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {ssoConfigs.map(cfg => (
                  <tr key={cfg.provider} style={{ borderBottom: '1px solid var(--border-color)' }}>
                    <td style={{ padding: '10px', fontWeight: 500 }}>{cfg.provider.toUpperCase()}</td>
                    <td style={{ padding: '10px', color: 'var(--text-tertiary)', fontFamily: 'monospace', fontSize: '12px' }}>{cfg.config?.label || cfg.config?.host || cfg.config?.discovery_url || '—'}</td>
                    <td style={{ padding: '10px' }}>{cfg.enabled ? 'Yes' : 'No'}</td>
                    <td style={{ padding: '10px' }}>{cfg.enforce ? <span style={{ color: 'var(--warning-color, orange)', fontWeight: 600 }}>Yes</span> : 'No'}</td>
                    <td style={{ padding: '10px', display: 'flex', gap: '6px' }}>
                      <button onClick={() => handleSSOEdit(cfg)} style={{ background: 'var(--accent-color)', color: 'var(--text-inverse)', border: 'none', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>Edit</button>
                      <button onClick={() => handleSSODelete(cfg.provider)} style={{ background: 'var(--error-color)', color: 'var(--text-inverse)', border: 'none', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </div>

        {/* ── White-Label / Custom Branding ── */}
        <div style={cardStyle}>
          <h3 style={{ marginTop: 0, marginBottom: '6px', color: 'var(--text-primary)' }}>White-Label / Custom Branding</h3>
          <p style={{ margin: '0 0 16px', fontSize: '13px', color: 'var(--text-tertiary)' }}>
            Customize app name, logo, favicon, accent color, and footer. Changes are served via <code>/api/branding</code> before login.
            Env vars <code>XAVVY_BRAND_NAME</code> / <code>XAVVY_BRAND_LOGO_URL</code> override these settings.
          </p>
          {brandingMsg && (
            <div style={{ padding: '10px', borderRadius: '4px', marginBottom: '12px', background: brandingMsg.type === 'success' ? 'rgba(40,167,69,0.1)' : 'rgba(220,53,69,0.1)', color: brandingMsg.type === 'success' ? 'var(--success-color)' : 'var(--error-color)', border: `1px solid ${brandingMsg.type === 'success' ? 'var(--success-color)' : 'var(--error-color)'}` }}>
              {brandingMsg.text}
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            {[['app_name', 'App Name', 'xavvy'], ['logo_url', 'Logo URL', 'https://…/logo.png'], ['favicon_url', 'Favicon URL', 'https://…/favicon.ico'], ['accent_color', 'Accent Color (hex)', '#5B4FE9']].map(([k, label, ph]) => (
              <div key={k}>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>{label}</div>
                <input value={branding[k]} onChange={e => setBranding(b => ({ ...b, [k]: e.target.value }))} style={inputStyle} placeholder={ph} />
              </div>
            ))}
          </div>
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Footer Text</div>
            <input value={branding.footer_text} onChange={e => setBranding(b => ({ ...b, footer_text: e.target.value }))} style={inputStyle} placeholder="Powered by Acme Corp" />
          </div>
          <button
            disabled={brandingSaving}
            onClick={async () => {
              setBrandingSaving(true); setBrandingMsg(null);
              try {
                const token = localStorage.getItem('token');
                await axios.post(`${API_BASE_URL}/admin/branding`, branding, { headers: { Authorization: `Bearer ${token}` } });
                setBrandingMsg({ type: 'success', text: 'Branding saved.' });
              } catch (err) {
                setBrandingMsg({ type: 'error', text: err.response?.data?.detail || 'Failed to save.' });
              } finally { setBrandingSaving(false); }
            }}
            style={{ ...btnStyle('var(--accent-color)', brandingSaving), width: 'auto', padding: '8px 20px', fontSize: '13px' }}
          >{brandingSaving ? 'Saving…' : 'Save Branding'}</button>
        </div>

        {/* ── IP Allowlisting & Network Policies ── */}
        <div style={cardStyle}>
          <h3 style={{ marginTop: 0, marginBottom: '6px', color: 'var(--text-primary)' }}>IP Allowlisting & Network Policies</h3>
          <p style={{ margin: '0 0 16px', fontSize: '13px', color: 'var(--text-tertiary)' }}>
            Restrict API access to specific IP ranges (CIDR notation, one per line). Inbound webhooks, form submissions,
            OAuth callbacks and the login endpoint are always exempted when <em>Public Endpoint Bypass</em> is on.
            List trusted reverse-proxy IPs so <code>X-Forwarded-For</code> is respected.
          </p>
          {netPolicyMsg && (
            <div style={{ padding: '10px', borderRadius: '4px', marginBottom: '12px', background: netPolicyMsg.type === 'success' ? 'rgba(40,167,69,0.1)' : 'rgba(220,53,69,0.1)', color: netPolicyMsg.type === 'success' ? 'var(--success-color)' : 'var(--error-color)', border: `1px solid ${netPolicyMsg.type === 'success' ? 'var(--success-color)' : 'var(--error-color)'}` }}>
              {netPolicyMsg.text}
            </div>
          )}
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px', cursor: 'pointer', fontSize: '13px', color: netPolicy.enabled ? 'var(--warning-color)' : 'var(--text-secondary)', fontWeight: netPolicy.enabled ? 600 : 400 }}>
            <input type="checkbox" checked={netPolicy.enabled} onChange={e => setNetPolicy(p => ({ ...p, enabled: e.target.checked }))} />
            {netPolicy.enabled ? '⚠ Allowlist enforcement is ENABLED' : 'Enable IP Allowlist'}
          </label>
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>CIDR Allowlist (one per line, e.g. <code>10.0.0.0/8</code>, <code>203.0.113.5/32</code>)</div>
            <textarea value={netPolicy.cidr_allowlist} onChange={e => setNetPolicy(p => ({ ...p, cidr_allowlist: e.target.value }))} style={{ ...inputStyle, height: '90px', fontFamily: 'monospace', fontSize: '12px', resize: 'vertical' }} placeholder={'10.0.0.0/8\n203.0.113.0/24'} />
          </div>
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Trusted Proxy IPs (one per line — enables X-Forwarded-For trust)</div>
            <textarea value={netPolicy.trusted_proxies} onChange={e => setNetPolicy(p => ({ ...p, trusted_proxies: e.target.value }))} style={{ ...inputStyle, height: '60px', fontFamily: 'monospace', fontSize: '12px', resize: 'vertical' }} placeholder="10.0.0.1" />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px', cursor: 'pointer', fontSize: '13px', color: 'var(--text-secondary)' }}>
            <input type="checkbox" checked={netPolicy.public_endpoint_bypass} onChange={e => setNetPolicy(p => ({ ...p, public_endpoint_bypass: e.target.checked }))} />
            Public Endpoint Bypass (allow webhooks / forms / OAuth / login from any IP)
          </label>
          <button
            disabled={netPolicySaving}
            onClick={async () => {
              setNetPolicySaving(true); setNetPolicyMsg(null);
              try {
                const token = localStorage.getItem('token');
                const cidrs = netPolicy.cidr_allowlist.split('\n').map(s => s.trim()).filter(Boolean);
                const proxies = netPolicy.trusted_proxies.split('\n').map(s => s.trim()).filter(Boolean);
                await axios.post(`${API_BASE_URL}/admin/network-policy`, {
                  enabled: netPolicy.enabled, cidr_allowlist: cidrs,
                  public_endpoint_bypass: netPolicy.public_endpoint_bypass, trusted_proxies: proxies,
                }, { headers: { Authorization: `Bearer ${token}` } });
                setNetPolicyMsg({ type: 'success', text: 'Network policy saved.' });
              } catch (err) {
                setNetPolicyMsg({ type: 'error', text: err.response?.data?.detail || 'Failed to save.' });
              } finally { setNetPolicySaving(false); }
            }}
            style={{ ...btnStyle('var(--accent-color)', netPolicySaving), width: 'auto', padding: '8px 20px', fontSize: '13px' }}
          >{netPolicySaving ? 'Saving…' : 'Save Network Policy'}</button>
        </div>

        {/* ── Audit Log Streaming / SIEM ── */}
        <div style={cardStyle}>
          <h3 style={{ marginTop: 0, marginBottom: '6px', color: 'var(--text-primary)' }}>Audit Log Streaming / SIEM</h3>
          <p style={{ margin: '0 0 16px', fontSize: '13px', color: 'var(--text-tertiary)' }}>
            Stream audit events in real time to a SIEM receiver (Splunk HEC, Datadog HTTP Intake, Elastic, or any custom endpoint).
            Events are delivered with up to 3 retries on failure.
          </p>
          {siemMsg && (
            <div style={{ padding: '10px', borderRadius: '4px', marginBottom: '12px', background: siemMsg.type === 'success' ? 'rgba(40,167,69,0.1)' : 'rgba(220,53,69,0.1)', color: siemMsg.type === 'success' ? 'var(--success-color)' : 'var(--error-color)', border: `1px solid ${siemMsg.type === 'success' ? 'var(--success-color)' : 'var(--error-color)'}` }}>
              {siemMsg.text}
            </div>
          )}
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Destination URL</div>
            <input value={siemConfig.destination_url} onChange={e => setSiemConfig(c => ({ ...c, destination_url: e.target.value }))} style={inputStyle} placeholder="https://splunk.example.com:8088/services/collector/event" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Secret Header Name <span style={{ opacity: 0.6 }}>(optional)</span></div>
              <input value={siemConfig.secret_header_name} onChange={e => setSiemConfig(c => ({ ...c, secret_header_name: e.target.value }))} style={inputStyle} placeholder="X-Splunk-Token" />
            </div>
            <div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Secret Header Value</div>
              <input type="password" value={siemConfig.secret_header_value} onChange={e => setSiemConfig(c => ({ ...c, secret_header_value: e.target.value }))} style={inputStyle} placeholder={siemConfigured ? '(unchanged)' : ''} />
            </div>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Event Types to Stream <span style={{ opacity: 0.6 }}>(comma-separated; blank = all events)</span></div>
            <input value={siemConfig.event_types} onChange={e => setSiemConfig(c => ({ ...c, event_types: e.target.value }))} style={inputStyle} placeholder="CREATE, UPDATE, DELETE, RUN, LOGIN" />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px', cursor: 'pointer', fontSize: '13px', color: 'var(--text-secondary)' }}>
            <input type="checkbox" checked={siemConfig.enabled} onChange={e => setSiemConfig(c => ({ ...c, enabled: e.target.checked }))} />
            Enable streaming
          </label>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              disabled={siemSaving}
              onClick={async () => {
                setSiemSaving(true); setSiemMsg(null);
                try {
                  const token = localStorage.getItem('token');
                  const eventTypes = siemConfig.event_types ? siemConfig.event_types.split(',').map(s => s.trim()).filter(Boolean) : null;
                  await axios.post(`${API_BASE_URL}/admin/audit/stream-config`, {
                    destination_url: siemConfig.destination_url,
                    secret_header_name: siemConfig.secret_header_name || null,
                    secret_header_value: siemConfig.secret_header_value || null,
                    enabled: siemConfig.enabled,
                    event_types: eventTypes,
                  }, { headers: { Authorization: `Bearer ${token}` } });
                  setSiemMsg({ type: 'success', text: 'SIEM streaming config saved.' });
                  setSiemConfigured(true);
                } catch (err) {
                  setSiemMsg({ type: 'error', text: err.response?.data?.detail || 'Failed to save.' });
                } finally { setSiemSaving(false); }
              }}
              style={{ ...btnStyle('var(--accent-color)', siemSaving), width: 'auto', padding: '8px 20px', fontSize: '13px' }}
            >{siemSaving ? 'Saving…' : 'Save SIEM Config'}</button>
            {siemConfigured && (
              <button
                onClick={async () => {
                  if (!(await window.confirm('Remove SIEM streaming configuration?'))) return;
                  try {
                    const token = localStorage.getItem('token');
                    await axios.delete(`${API_BASE_URL}/admin/audit/stream-config`, { headers: { Authorization: `Bearer ${token}` } });
                    setSiemConfigured(false); setSiemConfig({ destination_url: '', secret_header_name: '', secret_header_value: '', enabled: true, event_types: '' });
                    setSiemMsg({ type: 'success', text: 'SIEM config removed.' });
                  } catch (err) { setSiemMsg({ type: 'error', text: 'Failed to remove.' }); }
                }}
                style={{ ...btnStyle('var(--error-color)'), width: 'auto', padding: '8px 20px', fontSize: '13px' }}
              >Remove</button>
            )}
          </div>
        </div>

        {/* ── Embedded Builder SDK ── */}
        <div style={cardStyle}>
          <h3 style={{ marginTop: 0, marginBottom: '6px', color: 'var(--text-primary)' }}>Embedded Builder SDK</h3>
          <p style={{ margin: '0 0 16px', fontSize: '13px', color: 'var(--text-tertiary)' }}>
            Embed the xavvy editor inside partner applications. Your server provisions short-lived session tokens;
            the browser SDK loads the editor in a sandboxed iframe.
          </p>
          <div style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '6px', padding: '14px 16px', marginBottom: '14px' }}>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px', fontWeight: 600 }}>Step 1 — Set the server-side API key</div>
            <code style={{ fontSize: '12px', color: 'var(--accent-color)', display: 'block', marginBottom: '12px' }}>XAVVY_EMBED_API_KEY=your-secret-key</code>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px', fontWeight: 600 }}>Step 2 — Provision a session token (server-side only)</div>
            <pre style={{ fontSize: '11px', color: 'var(--text-primary)', background: 'var(--bg-tertiary)', padding: '10px', borderRadius: '4px', overflowX: 'auto', marginBottom: '12px' }}>{`POST /embed/session
Authorization: Bearer <XAVVY_EMBED_API_KEY>

{
  "workspace_id": "ws-uuid",
  "allowed_node_types": ["webhook","http_request","openai_agent"],
  "theme_overrides": { "--accent-color": "#e05c2a" },
  "ttl_seconds": 3600
}

→ { "token": "abc…", "expires_in": 3600 }`}</pre>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px', fontWeight: 600 }}>Step 3 — Load the SDK in your frontend</div>
            <pre style={{ fontSize: '11px', color: 'var(--text-primary)', background: 'var(--bg-tertiary)', padding: '10px', borderRadius: '4px', overflowX: 'auto', marginBottom: '12px' }}>{`<script src="${window.location.origin}/embed.js"></script>
<div id="xavvy-embed"></div>
<script>
  XavvyEmbed.init({
    container: '#xavvy-embed',
    baseUrl: '${window.location.origin}',
    token: '<session-token-from-step-2>',
    onSave: (wf) => console.log('saved', wf.id),
    onRunComplete: (result) => console.log('run done', result),
  });
</script>`}</pre>
            <div style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>
              Events: <code>workflow:saved</code>, <code>workflow:run</code>, <code>run:completed</code>, <code>run:failed</code> — dispatched via <code>postMessage</code>.
              Revoke a token early via <code>DELETE /embed/session/{'<token>'}</code>.
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
